import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useCabinet } from "../../context/CabinetContext";
import { avatarInitialColor } from "./cabinetUtils";
import CabIcon from "./CabIcon";
import SupportFab from "../support/SupportFab";
import logo from "../../assets/logo.png";
import { ECO_URLS, trackEcoClick } from "../../lib/ecoTrack";
import { themeVars, isDarkTheme } from "../../theme/cabThemes";
import { navFor, tabBarFor, getStoredMode, storeMode } from "../../app/nav";
import ModeSwitch from "./ModeSwitch";
import "../../styles/cabinet.css";
import "../../styles/support.css";


/**
 * Каркас кабинета Рублокса. Адаптивный:
 *  - десктоп (>880px): боковое меню слева (как exe-клиент)
 *  - мобила (≤880px): нижний таб-бар (как apk)
 *
 * Навигация между экранами — через активный таб (state в CabinetPage),
 * детальная страница игры скрывает меню/таб-бар.
 *
 * props:
 *   tabs        — [{id, label, emoji}]
 *   activeTab   — id текущего экрана
 *   onTabChange — (id) => void
 *   skinAvatar  — url avatar.png выбранного скина (для вкладки «Аватар»)
 *   fullscreen  — true на странице игры: прячет меню/таб-бар
 *   children    — контент экрана
 */
export default function CabinetLayout({
  tabs,
  activeTab,
  activeSub,            // активный more-подраздел (wiki/about/help) — для подсветки
  onTabChange,
  onOpenMoreSub,
  skinAvatar,
  fullscreen = false,
  children,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname;
  const { logout, isAuthenticated } = useAuth();
  const {
    diamonds, stars, rating, displayName, photo, effectiveTheme,
  } = useCabinet();

  /* Режим «Играю / Создаю» — подменяет группу разделов в меню.
     Хранится локально: выбор человека не должен сбрасываться при F5. */
  const [mode, setMode] = useState(getStoredMode);
  const navGroups = navFor(mode, isAuthenticated);
  const tabBar = tabBarFor(mode, isAuthenticated);

  const changeMode = (m) => {
    setMode(m);
    storeMode(m);
    // Уводим на главную режима: оставаться на «Моих играх» после
    // переключения в «Играю» — значит показывать раздел, которого
    // в текущем меню уже нет.
    navigate(m === "create" ? "/app/my" : "/app");
  };

  /* Переход по пункту меню. Внешние продукты (Студия) открываются
     через SSO-тикет в новой вкладке, а не роутером. */
  const goNav = (item) => {
    if (item.eco) {
      trackEcoClick(item.eco);
      window.open(ECO_URLS[item.eco] || "/", "_blank", "noopener");
      return;
    }
    navigate(item.to);
  };

  const handleLogout = () => {
    logout();
    navigate("/", { replace: true });
  };

  // аватар пользователя — фото или кружок-инициал
  const initial = (displayName || "?").charAt(0).toUpperCase();
  const initialBg = avatarInitialColor(displayName || "?");

  // Тема кабинета: CSS-переменные накладываются на корень .rb-cab.
  // Красится только внутренняя часть (/app) — лендинг/студия/плеер не трогаем.
  /* effectiveTheme, а не appTheme: во время примерки в магазине красим
     кабинет примеряемой темой — иначе «Примерить» не показывает ничего,
     кроме плашки, и примерки фактически нет. */
  const themeStyle = themeVars(effectiveTheme);
  /* Флаг тёмной темы нужен CSS, а не JS: на тёмном фоне тень и заливки
     состояний подменяются целиком (мягкая цветная тень там не видна,
     светлая заливка слепит). Класс — единственный способ сообщить об
     этом стилям, потому что сами палитры задаются инлайном. */
  const cabClass = `rb-cab${isDarkTheme(effectiveTheme) ? " is-dark" : ""}`;

  if (fullscreen) {
    // на странице игры — без меню, контент на всю ширину
    return <div className={cabClass} style={themeStyle}>{children}</div>;
  }

  return (
    <div className={cabClass} style={themeStyle}>
      {/* Плавающая кнопка выхода — зафиксирована в правом верхнем углу окна.
          В скроллящемся меню «Выйти» внизу часто не видно, поэтому дублируем
          здесь. Ненавязчивая: тёмная градиентная рамка, проявляется при
          наведении. */}
      <button
        className="rb-cab__logout-fab"
        onClick={handleLogout}
        title="Выйти из аккаунта"
        aria-label="Выйти из аккаунта"
      >
        <CabIcon name="exit" size={18} />
        <span className="rb-cab__logout-fab-label">Выйти</span>
      </button>

      <div className="rb-cab__shell">
        {/* ── боковое меню (десктоп) ──────────────────────────────── */}
        <aside className="rb-cab__sidebar">
          <div className="rb-cab__brand">
            <img src={logo} alt="Рублокс" />
            <span className="rb-cab__brand-name">Рублокс</span>
          </div>

          {/* профиль-чип (клик → страница профиля) */}
          {/* Подсветка по "me": чип ведёт в личный профиль, туда же, куда
              пункт меню «Профиль». Раньше сравнивалось с "profile" —
              вкладкой, которой в этом меню нет, поэтому чип не
              подсвечивался никогда. */}
          <button
            className={`rb-cab__me ${activeTab === "me" ? "is-active" : ""}`}
            onClick={() => onTabChange("me")}
            title="Мой профиль"
          >
            {photo ? (
              <img
                className="rb-cab__me-avatar"
                src={photo}
                alt={displayName}
              />
            ) : (
              <span
                className="rb-cab__me-avatar"
                style={{ background: initialBg }}
              >
                {initial}
              </span>
            )}
            <div className="rb-cab__me-info">
              <div className="rb-cab__me-name">{displayName}</div>
              <div className="rb-cab__me-econ">
                <span>
                  <CabIcon name="diamond" size={13} />
                  {diamonds == null ? "—" : diamonds}
                </span>
                <span>
                  <CabIcon name="star" size={13} />
                  {stars == null ? "—" : stars}
                </span>
                <span>
                  <CabIcon name="trophy" size={13} />
                  {rating == null ? "—" : rating}
                </span>
              </div>
            </div>
            <CabIcon name="arrowRight" size={14} className="rb-cab__me-chev" />
          </button>

          <ModeSwitch mode={mode} onChange={changeMode} />

          {/* Навигация из app/nav.js — единый источник правды.
              Раньше список вкладок был продублирован в четырёх местах
              двумя разными наборами, из-за чего часть разделов была
              недостижима из кабинета. */}
          <nav className="rb-cab__nav">
            {navGroups.map((group) => (
              <div key={group.label} className="rb-cab__nav-group">
                <div className="rb-cab__nav-label">{group.label}</div>
                {group.items.map((item) => {
                  const active = item.end
                    ? path === item.to
                    : path.startsWith(item.to);
                  return (
                    <button
                      key={item.to}
                      className={`rb-cab__nav-item ${active ? "is-active" : ""}`}
                      onClick={() => goNav(item)}
                      title={item.label}
                    >
                      <span className="rb-cab__nav-ico">
                        <CabIcon name={item.icon} size={20} />
                      </span>
                      {item.label}
                      {item.eco && (
                        <CabIcon
                          name="arrowRight"
                          size={13}
                          className="rb-cab__nav-ext"
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            ))}
          </nav>

          {/* низ сайдбара */}
          <div className="rb-cab__sidebar-foot">
            {/* «Скачать APK» убран из десктопного сайдбара 2026-06-16 —
                с компьютера APK не нужен (это мобильное приложение). Скачать
                его всё равно можно со страницы /download. На мобиле меню
                другое (таб-бар), этот сайдбар там не показывается. */}
            <a
              className="rb-cab__nav-item"
              href={ECO_URLS.minecraftia}
              target="_blank"
              rel="noreferrer"
              data-mtrk-click="eco:minecraftia:sidebar"
              onClick={() => trackEcoClick("minecraftia", "sidebar")}
            >
              <span className="rb-cab__nav-ico">
                <CabIcon name="globe" size={20} />
              </span>
              Сайт школы
            </a>
            {/* Пункт «Маська» убран из меню: интеграция будет сделана
                иначе, а ссылка «наружу» в списке разделов кабинета сбивала
                с толку — она уводила с сайта, стоя рядом с внутренними
                разделами. */}
            <button className="rb-cab__nav-item" onClick={handleLogout}>
              <span className="rb-cab__nav-ico">
                <CabIcon name="exit" size={20} />
              </span>
              Выйти
            </button>
          </div>
        </aside>

        {/* ── контент экрана ──────────────────────────────────────── */}
        <main className="rb-cab__content">
          <div className="rb-cab__inner">{children}</div>
        </main>
      </div>

      {/* ── нижний таб-бар (мобила) ───────────────────────────────── */}
      {/* Мобильный таб-бар — из того же источника, что сайдбар.
          Раньше здесь был свой набор вкладок, из-за чего меню на
          телефоне и на компьютере вели в разные места. */}
      <nav className="rb-cab__tabbar">
        {tabBar.map((item) => {
          const active = item.end ? path === item.to : path.startsWith(item.to);
          return (
            <button
              key={item.to}
              className={`rb-cab__tab ${active ? "is-active" : ""}`}
              onClick={() => goNav(item)}
            >
              <span className="rb-cab__tab-ico">
                <CabIcon name={item.icon} size={22} />
              </span>
              <span className="rb-cab__tab-label">{item.label}</span>
            </button>
          );
        })}
      </nav>

      {/* Плавающая кнопка «Связаться с разрабом» — Аудит 3, Неделя 4.
          На fullscreen-странице игры не рендерится (см. ранний return). */}
      <SupportFab />
    </div>
  );
}
